/**
 * Payment plans with the tax offices, one row per plan (Fan, 2026-10-28:
 * "put like a table in place where the payment plans are shown").
 * Rows come from lib/taxReturns buildPaymentPlans. Read-only. A plan letter
 * on file (tax_obligations.documents) opens from the paperclip (Fan, 2026-10-29).
 */
import React from 'react';
import { Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { payrollAPI } from '../../lib/api/payroll';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { SectionCard } from './TaxDashboardParts';

function fmtDate(d) {
  if (!d) return null;
  const [y, m, dd] = String(d).slice(0, 10).split('-');
  return dd ? `${dd}/${m}/${y}` : d;
}

const DASH = '\u2014';

async function openPlanFile(obId, index) {
  try {
    const res = await payrollAPI.getTaxObligationFile(obId, index);
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    toast.error('Could not open the letter');
  }
}

export function PaymentPlansTable({ plans = [], nativeFmt, regionLabel }) {
  if (!plans.length) return null;
  const money = (v, c, empty = 'Not on file') => (v == null ? <span className="text-muted-foreground text-xs">{empty}</span> : nativeFmt(v, c));
  const totals = {};
  plans.forEach((p) => {
    const t = totals[p.currency] || (totals[p.currency] = { left: 0, monthly: 0 });
    t.left += p.left || 0;
    if (p.status === 'running' && p.instalment) t.monthly += p.instalment;
  });
  const joinTot = (k) => Object.entries(totals).filter(([, t]) => t[k]).map(([c, t]) => nativeFmt(t[k], c)).join(' + ') || DASH;
  return (
    <SectionCard
      title={`Payment plans${regionLabel ? ` (${regionLabel})` : ''}`}
      subtitle="Every plan agreed with a tax office: what it covers, what is paid, what is left and the next payment."
      testId="tax-payment-plans"
    >
      <div className="overflow-x-auto">
        <Table className="table-fixed min-w-[60rem]">
          <colgroup>
            <col className="w-[11rem]" />
            <col />
            <col className="w-[8rem]" />
            <col className="w-[8rem]" />
            <col className="w-[8rem]" />
            <col className="w-[8rem]" />
            <col className="w-[9rem]" />
          </colgroup>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Left</TableHead>
              <TableHead className="text-right">Instalment</TableHead>
              <TableHead>Next payment</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {plans.map((p) => (
              <TableRow key={p.id} data-testid={`plan-row-${p.id}`}>
                <TableCell className="align-top whitespace-normal font-medium">{p.entity_name}</TableCell>
                <TableCell className="align-top whitespace-normal">
                  <div>
                    {p.authority}: {p.covers}
                    {(p.documents || []).map((d) => (
                      <button
                        key={d.index}
                        type="button"
                        className="ml-2 inline-flex items-center gap-0.5 text-xs text-primary hover:underline"
                        onClick={() => openPlanFile(p.doc_ob_id, d.index)}
                        title={d.filename}
                        data-testid={`plan-file-${p.id}-${d.index}`}
                      >
                        <Paperclip className="h-3 w-3" />Letter
                      </button>
                    ))}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {[p.start_date && `From ${fmtDate(p.start_date)}`, p.instalments, p.method,
                      p.end_date && `last ${fmtDate(p.end_date)}`].filter(Boolean).join(', ')}
                  </div>
                </TableCell>
                <TableCell className="align-top text-right font-mono">{money(p.agreed, p.currency)}</TableCell>
                <TableCell className="align-top text-right font-mono text-green-700">{p.paid ? nativeFmt(p.paid, p.currency) : DASH}</TableCell>
                <TableCell className="align-top text-right font-mono text-red-600">{money(p.left, p.currency)}</TableCell>
                <TableCell className="align-top text-right font-mono">{money(p.instalment, p.currency, 'Not set')}</TableCell>
                <TableCell className="align-top whitespace-normal">
                  {p.next_date
                    ? <>{fmtDate(p.next_date)}{p.next_estimated && <span className="text-xs text-muted-foreground"> (expected)</span>}</>
                    : <span className="text-muted-foreground text-xs">No date set</span>}
                </TableCell>
              </TableRow>
            ))}
            <TableRow className="font-semibold border-t-2">
              <TableCell>Total</TableCell>
              <TableCell className="text-xs text-muted-foreground font-normal whitespace-normal">{plans.length} plans; Left counts only plans with a known balance</TableCell>
              <TableCell />
              <TableCell />
              <TableCell className="text-right font-mono text-red-600">{joinTot('left')}</TableCell>
              <TableCell className="text-right font-mono">{joinTot('monthly')}</TableCell>
              <TableCell className="text-xs text-muted-foreground font-normal">a month</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </SectionCard>
  );
}
